import { Component, inject, OnInit, HostListener  } from '@angular/core';
import { NgFor, NgIf, CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService } from '../../../core/services/auth.service';
import { UsersService } from '../../../core/services/users.service';
import { AuthUser } from '../../../core/models/auth/auth.interface';
import { User } from '../../../core/models/user.interface';
import { BaseComponent } from '../../../core/shared/base.component';
import { LoaderService } from '../../../core/shared/services/loader.service';
import { FormsModule } from '@angular/forms';

interface Question {
  id: number;
  title: string;
  subtitle: string;
  type: 'dropdown' | 'box';
  answers: string[];
  value: string;
  isOpen: boolean;
  otherAnswer: boolean;
  otherAnswerValue: string;
}

interface QuestionPage {
  questions: Question[];
}

@Component({
  selector: 'app-complete-data',
  standalone: true,
  imports: [NgFor, NgIf, CommonModule, FormsModule],
  templateUrl: './complete-data.component.html',
  styleUrl: './complete-data.component.scss'
})
export class CompleteDataComponent extends BaseComponent implements OnInit {
  private loader = inject(LoaderService);
  private authService: AuthService = inject(AuthService);

  authUser: AuthUser = this.authService.getAuthUser()!;
  user: User = this.authService.getUser()!;
  
  currentQuestionPageIndex: number = 0;
  selectedAnswer: string | null = null;
  showModal: boolean = false;

  questionPages: QuestionPage[] = [];

  //this variable is so we only have 1 dropdown opened at the same time
  openDropdownIndex: number | null = null;

  constructor(private router: Router, private usersService: UsersService) {
    super()
  }
  async ngOnInit(): Promise<void> {
    if (this.user?.profile && this.user?.profile?.registerQuestionsComplete) 
      this.router.navigate(['/assessment']);

    this.questionPages = this.convertQuestionsToPages(this.user?.profile?.registerQuestions);
  }

  //IMPORTANT!!!!: the reason we set the variables here and not store them in the database is
  //               because we can't modify the database schema.
  //               Instead, given that the data is "static", we are setting it here.
  //               THIS IS BAD, BUT I DON'T HAVE TIME TO THINK OF A BETTER OPTION
  convertQuestionsToPages(questions: any[]): QuestionPage[] {
    if (!questions || questions.length === 0) return [];
    
    //we format the questions with data required for the html
    const formattedQuestions: Question[] = questions.map((question, index) => {
      let id = index;

      // Determine the type
      const type = index < 2 ? 'box' : 'dropdown';
    
      // Extract subtitle (only for question 5)
      let title = question.title;
      let subtitle = '';
      if (index === 4 && question.title.includes('?')) {
        const parts = question.title.split('?');
        title = parts[0].trim() + '?';
        subtitle = parts[1].trim();
      }

      //other answer (only for question 6)
      let otherAnswer: boolean = index === 5;
    
      return {
        id,
        title,
        subtitle,
        type,
        answers: question.answers || [],
        value: question.value || '',
        isOpen: false,
        otherAnswer: otherAnswer,
        otherAnswerValue: ''
      };
    });
  
    // Split into two pages
    const pages: QuestionPage[] = [
      { questions: formattedQuestions.slice(0, 3) },
      { questions: formattedQuestions.slice(3, 6) }
    ];
  
    console.log('Formatted pages:', pages);
    return pages;
  }

  convertPagesToQuestions(): any[] {
    const allQuestions: any[] = this.questionPages
      .flatMap(page => page.questions)
      .map(question => {
        let title = question.subtitle ? `${question.title} ${question.subtitle}` : question.title;

        // Handle custom "other" answers
        if (question.otherAnswer && question.value === question.answers[question.answers.length - 1]) {
          // Add it to the list of answers
          if (!question.answers.includes(question.otherAnswerValue)) {
            question.answers.push(question.otherAnswerValue);
          }
          // Make it the selected value
          question.value = question.otherAnswerValue;
        }

        return {
          title,
          answers: question.answers,
          value: question.value
        };
      });

    return allQuestions;
  }

  get currentQuestionPage() {
    const questionPage = this.questionPages[this.currentQuestionPageIndex];
    return questionPage;
  }

  toggleDropdown(questionIndex: number) {
    if (this.openDropdownIndex === questionIndex)
      // Close if same one is open
      this.openDropdownIndex = null;
    
    // Open only that one
    this.openDropdownIndex = questionIndex;
  }

  // Detect click outside any dropdown
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest('.custom-dropdown')) {
      this.openDropdownIndex = null;
    }
  }

  selectAnswer(answer: string, question: Question) {
    question.value = answer;
    this.openDropdownIndex = null;
  }

  areAllAnswered(): boolean {
    const page = this.currentQuestionPage;
    if (!page || !page.questions) return false;

    return page.questions.every(q => {
      // If the question has an "Other" option selected,
      // ensure that the user has typed something in `otherAnswerValue`
      if (q.otherAnswer && q.value === q.answers[q.answers.length - 1]) {
        return q.otherAnswerValue.trim() !== '';
      }
      // Otherwise, the question is considered answered
      // if `q.value` is not null, undefined, or an empty string
      return q.value !== null && q.value !== undefined && q.value !== '';
    });
  }

  get userName(): string {
    return this.user?.name || this.user?.firstName || 'Usuario';
  }

  showWelcomeModal() {
    if (!this.areAllAnswered()) {
      //TODO: convert this console.log into an alert
      console.log('Por favor selecciona una respuesta para todas las preguntas de la página');
      return;
    }

    this.loader.show();

    const payload = this.convertPagesToQuestions();
        
    this.usersService.updateRegisterQuestions(this.authUser!!.id, payload)
      .subscribe({
        next: async res => {
          // Reset selection state if you want (optional)
          // this.questionPages.forEach(p => p.questions.forEach(q => q.value = ''));

          // Navigate pages / show modal
          if (this.currentQuestionPageIndex >= this.questionPages.length - 1) {
            this.showModal = true;
          } else {
            this.currentQuestionPageIndex++;
            this.updateCarouselStep();
          }
          this.loader.hide();
        },
        error: err => {
          this.errorDefault(err);
          this.loader.hide();
        }
      });
      
  }

  closeModal() {
    this.showModal = false;
  }

  continueToNextForm() {
    this.showModal = false;
    this.router.navigate(['/assessment']);
  }

  updateCarouselStep() {
    if (this.currentQuestionPageIndex === 1) {
      this.router.navigate([], {
        queryParams: { updateStep: true }
      });
    }
  }
}